import { useMutation } from '@apollo/client'
import { SidebarMenuItem } from './SidebarMenuItem'
import { SidebarMenuItemWithSub } from './SidebarMenuItemWithSub'
import { useGetUser } from '../../../../../app/tfi/store/userStore'
import { hasPermission } from '../../../../../app/tfi/helpers'
import { ALL_PERMISSIONS, STORAGE_NAMES } from '../../../../../app/tfi/helpers/consts'
import { useRealtimeStore } from '../../../../../app/tfi/store/realtimeStore'
import { useUserPreferencesStore } from '../../../../../app/tfi/store/userPreferencesStore'
import { UPDATE_USER_PREFERENCES } from '../../../../../app/tfi/gql/mutations/userMutations'

// ─── Route metadata for the FAVORITOS section ─────────────────────────────────
const MENU_CONFIG: Record<string, { title: string; fontIcon: string }> = {
  '/dashboard': { title: 'Dashboards', fontIcon: 'bi-speedometer2' },
  '/leads': { title: 'Leads', fontIcon: 'bi-person-vcard' },
  '/contacts': { title: 'Contacts', fontIcon: 'bi-telephone-fill' },
  '/quotes': { title: 'Quotes', fontIcon: 'bi-clipboard-data' },
  '/policies': { title: 'Policies', fontIcon: 'bi-shield-check' },
  '/medicare-center': { title: 'Medicare Center', fontIcon: 'bi-hospital' },
  '/calendar': { title: 'Calendar', fontIcon: 'bi-calendar3' },
  '/kanban': { title: 'Tasks', fontIcon: 'bi-kanban' },
  '/campaigns': { title: 'Campaigns', fontIcon: 'bi-megaphone' },
  '/inbox': { title: 'Inbox', fontIcon: 'bi-chat-square-text' },
  '/email-inbox': { title: 'Gmail', fontIcon: 'bi-envelope' },
  '/policychat': { title: 'PolicyChat', fontIcon: 'bi-chat-heart' },
  '/notifications': { title: 'Notifications', fontIcon: 'bi-bell' },
  '/tickets': { title: 'Tickets', fontIcon: 'bi-ticket-perforated' },
  '/users': { title: 'Users', fontIcon: 'bi-people' },
  '/commissions': { title: 'Commissions', fontIcon: 'bi-piggy-bank' },
  '/signature-forms': { title: 'Digital Signatures', fontIcon: 'bi-file-earmark-check' },
  '/integrations': { title: 'Integrations', fontIcon: 'bi-diagram-3' },
  '/resource-center': { title: 'Resource Center', fontIcon: 'bi-folder2-open' },
  '/academy': { title: 'Academy', fontIcon: 'bi-mortarboard' },
}

// ─── Groups (menu items with a submenu) whose children can be favorited ───────
// A group is "promoted" into the Favorites section (as a full expandable
// accordion) once every one of its children is favorited. Until then, any
// favorited child is listed individually like a regular leaf favorite.
type MenuGroupChild = { route: string; title: string }
type MenuGroup = { title: string; fontIcon: string; icon: string; children: MenuGroupChild[] }

const MENU_GROUPS: Record<string, MenuGroup> = {
  '/dashboard': {
    title: 'Dashboards',
    fontIcon: 'bi-speedometer2',
    icon: 'element-plus',
    children: [
      { route: '/dashboard/policy', title: 'Dashboard based on status' },
      { route: '/dashboard/executive', title: 'Dashboard based on commissions' },
    ],
  },
  '/campaigns': {
    title: 'Campaigns',
    fontIcon: 'bi-megaphone',
    icon: 'element-plus',
    children: [
      { route: '/campaigns', title: 'Campaigns' },
      { route: '/templates', title: 'Templates' },
      { route: '/recipients', title: 'Recipients' },
      { route: '/media-manager', title: 'Media Manager' },
      { route: '/automations', title: 'Automations' },
    ],
  },
  '/signature-forms': {
    title: 'Digital Signatures',
    fontIcon: 'bi-file-earmark-check',
    icon: 'element-plus',
    children: [
      { route: '/signature-forms', title: 'Signature Forms' },
      { route: '/pdf-envelopes', title: 'PDF Signing' },
    ],
  },
}

// Route → display info for a group's child, used when it's listed individually.
const GROUP_CHILD_INFO: Record<string, { title: string; fontIcon: string }> = Object.values(
  MENU_GROUPS
).reduce((acc, group) => {
  group.children.forEach((child) => {
    acc[child.route] = { title: child.title, fontIcon: group.fontIcon }
  })
  return acc
}, {} as Record<string, { title: string; fontIcon: string }>)

// ─── Star button rendered as children inside .menu-item ───────────────────────
// Rendered as children of SidebarMenuItem so .menu-item stays a direct child
// of .menu — preserving Metronic's `.menu > .menu-item` CSS selector.
// Visibility on hover is handled by CSS in _tfi.scss, not React state.
const StarButton = ({ route }: { route: string }) => {
  const sidebarFavorites = useUserPreferencesStore((s) => s.sidebarFavorites)
  const toggleFavorite = useUserPreferencesStore((s) => s.toggleFavorite)
  const syncUserProfile = useGetUser((s) => s.syncUserProfile)
  const isFav = sidebarFavorites.includes(route)
  const [syncPrefs] = useMutation(UPDATE_USER_PREFERENCES)

  const handleClick = (e: { preventDefault(): void; stopPropagation(): void }) => {
    e.preventDefault()
    e.stopPropagation()
    toggleFavorite(route)
    // Read updated list synchronously — Zustand set() is synchronous
    const newFavorites = useUserPreferencesStore.getState().sidebarFavorites
    // Persist preferences into the stored user object so page reload hydrates correctly
    syncUserProfile({ preferences: { sidebar_favorites: newFavorites } })
    // Sync to backend (fire and forget — local state is the source of truth for UX)
    syncPrefs({
      variables: { preferences: JSON.stringify({ sidebar_favorites: newFavorites }) },
    }).catch(() => { })
  }

  return (
    <button
      onClick={handleClick}
      title={isFav ? 'Remove from favorites' : 'Add to favorites'}
      className={`btn-favorite-sidebar${isFav ? ' is-fav' : ''}`}
    >
      <i className={`bi ${isFav ? 'bi-star-fill' : 'bi-star'}`} />
    </button>
  )
}

// ─── Section header with expand/collapse ──────────────────────────────────────
const SectionHeader = ({
  title,
  isExpanded,
  onToggle,
}: {
  title: string
  isExpanded: boolean
  onToggle: () => void
}) => (
  <div className='menu-item cursor-pointer' onClick={onToggle} style={{ userSelect: 'none' }}>
    <div className='menu-content pt-4 pb-1 d-flex align-items-center justify-content-between pe-3'>
      <span className='menu-section text-muted text-uppercase fs-8 ls-1'>{title}</span>
      <i className={`bi fs-8 text-muted ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`} />
    </div>
  </div>
)

const SectionDivider = () => <div className='separator mx-3 my-1 opacity-10' />

const withStar = (route: string) => <StarButton route={route} />

// ─── Main component ───────────────────────────────────────────────────────────
const SidebarMenuMainNew = () => {
  const user = useGetUser((s) => s.user)
  const status = useGetUser((s) => s.status)
  const permissions = useGetUser((u) => u?.user?.permissions)

  const unreadCount = useRealtimeStore((s) => s.sidebarCounters.unreadNotificationsCount)
  const openTicketsCount = useRealtimeStore((s) => s.sidebarCounters.openTicketsCount)
  const inboxUnreadCount = useRealtimeStore((s) => s.sidebarCounters.inboxUnreadCount)
  const policyChatUnread = useRealtimeStore((s) => s.sidebarCounters.policyChatUnreadCount)

  const isGoogleConnected = Boolean(localStorage.getItem(STORAGE_NAMES.TOKEN_GOOGLE))

  const expandedSections = useUserPreferencesStore((s) => s.expandedSections)
  const toggleSection = useUserPreferencesStore((s) => s.toggleSection)
  const sidebarFavorites = useUserPreferencesStore((s) => s.sidebarFavorites)

  if (!permissions || !user || status !== 'user') return null

  const isExpanded = (id: string) => expandedSections.includes(id)

  // Groups whose every child is favorited get promoted to a full expandable
  // group inside Favorites; their child routes are excluded from the flat list.
  const promotedGroups = Object.entries(MENU_GROUPS).filter(
    ([, group]) =>
      group.children.length > 0 &&
      group.children.every((child) => sidebarFavorites.includes(child.route))
  )
  const promotedRoutes = new Set(
    promotedGroups.flatMap(([, group]) => group.children.map((child) => child.route))
  )

  const badgeFor = (route: string): number | undefined => {
    const map: Record<string, number> = {
      '/inbox': inboxUnreadCount,
      '/policychat': policyChatUnread,
      '/notifications': unreadCount,
      '/tickets': openTicketsCount,
    }
    const count = map[route]
    return count > 0 ? count : undefined
  }

  const hasDashboard = hasPermission(permissions, ALL_PERMISSIONS.VIEW_DASHBOARD)

  // El Inbox lista conversaciones armadas con CommunicationLog, así que se gatea
  // con el permiso de esa entidad. Ojo: hubo un view_inbox que parecía el permiso
  // obvio, pero la API — api/chat/permissions.py, que comparten REST y GraphQL —
  // nunca lo miró, así que gatear el menú con él mostraba un item que después caía
  // en NotFoundPage. Se eliminó en la migración 0334 del backend.
  const canViewInbox = hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMUNICATION_LOGS)

  const hasCrm =
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_LEAD) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY)

  const hasManagement =
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_USER) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMISSION) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_PDF_ENVELOPES) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_INTEGRATION) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_BILLING)

  const hasOperations =
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_CALENDAR) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_TASKS) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_CAMPAIGNS)

  const hasCommunication =
    canViewInbox ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICYCHAT) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_NOTIFICATION) ||
    hasPermission(permissions, ALL_PERMISSIONS.VIEW_TICKET)

  return (
    <>
      {/* ── FAVORITOS ─────────────────────────────────────────────────────── */}
      {sidebarFavorites.length > 0 && (
        <>
          <div className='menu-item'>
            <div className='menu-content pt-0 pb-1'>
              <span className='menu-section text-muted text-uppercase fs-8 ls-1'>
                <i className='bi bi-star-fill me-1' style={{ color: '#f6c000', fontSize: 9 }} />
                Favorites
              </span>
            </div>
          </div>
          {promotedGroups.map(([key, group]) => (
            <SidebarMenuItemWithSub
              key={key}
              to={key}
              title={group.title}
              fontIcon={group.fontIcon}
              icon={group.icon}
            >
              {group.children.map((child) => (
                <SidebarMenuItem key={child.route} to={child.route} title={child.title} hasBullet>
                  {withStar(child.route)}
                </SidebarMenuItem>
              ))}
            </SidebarMenuItemWithSub>
          ))}
          {sidebarFavorites
            .filter((route) => !promotedRoutes.has(route))
            .filter((route) => route !== '/inbox' || canViewInbox)
            .map((route) => {
              const cfg = GROUP_CHILD_INFO[route] || MENU_CONFIG[route]
              if (!cfg) return null
              return (
                <SidebarMenuItem
                  key={route}
                  to={route}
                  title={cfg.title}
                  fontIcon={cfg.fontIcon}
                  badge={badgeFor(route)}
                >
                  {withStar(route)}
                </SidebarMenuItem>
              )
            })}
          <SectionDivider />
        </>
      )}

      {/* ── OVERVIEW ──────────────────────────────────────────────────────── */}
      {hasDashboard && (
        <>
          <SectionHeader
            title='Overview'
            isExpanded={isExpanded('overview')}
            onToggle={() => toggleSection('overview')}
          />
          {isExpanded('overview') && (
            <SidebarMenuItemWithSub
              to='/dashboard'
              title='Dashboards'
              fontIcon='bi-speedometer2'
              icon='element-plus'
            >
              <SidebarMenuItem to='/dashboard/policy' title='Dashboard based on status' hasBullet>
                {withStar('/dashboard/policy')}
              </SidebarMenuItem>
              <SidebarMenuItem to='/dashboard/executive' title='Dashboard based on commissions' hasBullet>
                {withStar('/dashboard/executive')}
              </SidebarMenuItem>
            </SidebarMenuItemWithSub>
          )}
        </>
      )}

      {/* ── CRM ───────────────────────────────────────────────────────────── */}
      {hasCrm && (
        <>
          <SectionDivider />
          <SectionHeader
            title='CRM'
            isExpanded={isExpanded('crm')}
            onToggle={() => toggleSection('crm')}
          />
          {isExpanded('crm') && (
            <>
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_LEAD) && (
                <SidebarMenuItem to='/leads' title='Leads' fontIcon='bi-person-vcard'>
                  {withStar('/leads')}
                </SidebarMenuItem>
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT) && (
                <SidebarMenuItem to='/contacts' title='Contacts' fontIcon='bi-telephone-fill'>
                  {withStar('/contacts')}
                </SidebarMenuItem>
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY) && (
                <SidebarMenuItem to='/quotes' title='Quotes' fontIcon='bi-clipboard-data'>
                  {withStar('/quotes')}
                </SidebarMenuItem>
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICY) && (
                <SidebarMenuItem to='/policies' title='Policies' fontIcon='bi-shield-check'>
                  {withStar('/policies')}
                </SidebarMenuItem>
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_MEDICARE_CENTER) && (
                <SidebarMenuItem to='/medicare-center' title='Medicare Center' fontIcon='bi-hospital'>
                  {withStar('/medicare-center')}
                </SidebarMenuItem>
              )}
            </>
          )}
        </>
      )}

      {/* ── OPERATIONS ────────────────────────────────────────────────────── */}
      {hasOperations && (
      <>
        <SectionDivider />
        <SectionHeader
          title='Operations'
          isExpanded={isExpanded('operations')}
          onToggle={() => toggleSection('operations')}
        />
        {isExpanded('operations') && (
          <>
            {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CALENDAR) && (
              <SidebarMenuItem to='/calendar' title='Calendar' fontIcon='bi-calendar3'>
                {withStar('/calendar')}
              </SidebarMenuItem>
            )}
            {hasPermission(permissions, ALL_PERMISSIONS.VIEW_TASKS) && (
              <SidebarMenuItem to='/kanban' title='Tasks' fontIcon='bi-kanban'>
                {withStar('/kanban')}
              </SidebarMenuItem>
            )}
            {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CAMPAIGNS) && (
              <SidebarMenuItemWithSub
                to='/campaigns'
                title='Campaigns'
                fontIcon='bi-megaphone'
                icon='element-plus'
              >
                <SidebarMenuItem to='/campaigns' title='Campaigns' hasBullet>
                  {withStar('/campaigns')}
                </SidebarMenuItem>
                {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CAMPAIGN_TEMPLATES) && (
                  <SidebarMenuItem to='/templates' title='Templates' hasBullet>
                    {withStar('/templates')}
                  </SidebarMenuItem>
                )}
                {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CAMPAIGN_RECIPIENTS) && (
                  <SidebarMenuItem to='/recipients' title='Recipients' hasBullet>
                    {withStar('/recipients')}
                  </SidebarMenuItem>
                )}
                {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CAMPAIGN_MEDIA) && (
                  <SidebarMenuItem to='/media-manager' title='Media Manager' hasBullet>
                    {withStar('/media-manager')}
                  </SidebarMenuItem>
                )}
                {hasPermission(permissions, ALL_PERMISSIONS.VIEW_CAMPAIGN_AUTOMATIONS) && (
                  <SidebarMenuItem to='/automations' title='Automations' hasBullet>
                    {withStar('/automations')}
                  </SidebarMenuItem>
                )}
              </SidebarMenuItemWithSub>
            )}
          </>
        )}
      </>
      )}

      {/* ── COMMUNICATION ─────────────────────────────────────────────────── */}
      {hasCommunication && (
      <>
        <SectionDivider />
        <SectionHeader
          title='Communication'
          isExpanded={isExpanded('communication')}
          onToggle={() => toggleSection('communication')}
        />
        {isExpanded('communication') && (
          <>
            {canViewInbox && (
              <SidebarMenuItem to='/inbox' title='Inbox' fontIcon='bi-chat-square-text' badge={badgeFor('/inbox')}>
                {withStar('/inbox')}
              </SidebarMenuItem>
            )}
            {isGoogleConnected && (
              <SidebarMenuItem to='/email-inbox' title='Gmail' fontIcon='bi-envelope' badge={badgeFor('/email-inbox')}>
                {withStar('/email-inbox')}
              </SidebarMenuItem>
            )}
            {hasPermission(permissions, ALL_PERMISSIONS.VIEW_POLICYCHAT) && (
              <SidebarMenuItem to='/policychat' title='PolicyChat' fontIcon='bi-chat-heart' badge={badgeFor('/policychat')}>
                {withStar('/policychat')}
              </SidebarMenuItem>
            )}
            {hasPermission(permissions, ALL_PERMISSIONS.VIEW_NOTIFICATION) && (
              <SidebarMenuItem to='/notifications' title='Notifications' fontIcon='bi-bell' badge={badgeFor('/notifications')}>
                {withStar('/notifications')}
              </SidebarMenuItem>
            )}
            {hasPermission(permissions, ALL_PERMISSIONS.VIEW_TICKET) && (
              <SidebarMenuItem to='/tickets' title='Tickets' fontIcon='bi-ticket-perforated' badge={badgeFor('/tickets')}>
                {withStar('/tickets')}
              </SidebarMenuItem>
            )}
          </>
        )}
      </>
      )}

      {/* ── MANAGEMENT ────────────────────────────────────────────────────── */}
      {/* Todos los items de la sección están gateados por permiso y hasManagement
          es el OR de esos mismos permisos, así que no hay excepciones por rol:
          agregarlas mostraría el encabezado con la sección vacía. */}
      {hasManagement && (
        <>
          <SectionDivider />
          <SectionHeader
            title='Management'
            isExpanded={isExpanded('management')}
            onToggle={() => toggleSection('management')}
          />
          {isExpanded('management') && (
            <>
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_USER) && (
                <SidebarMenuItem to='/users' title='Users' fontIcon='bi-people'>
                  {withStar('/users')}
                </SidebarMenuItem>
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_BILLING) && (
                <SidebarMenuItem to='/billing' title='Billing' fontIcon='bi-cash-coin' />
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_COMMISSION) && (
                <SidebarMenuItem to='/commissions' title='Commissions' fontIcon='bi-piggy-bank'>
                  {withStar('/commissions')}
                </SidebarMenuItem>
              )}
              {(hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS) ||
                hasPermission(permissions, ALL_PERMISSIONS.VIEW_PDF_ENVELOPES)) && (
                  <SidebarMenuItemWithSub
                    to='/signature-forms'
                    title='Digital Signatures'
                    fontIcon='bi-file-earmark-check'
                    icon='element-plus'
                  >
                    {hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS) && (
                      <SidebarMenuItem to='/signature-forms' title='Signature Forms' hasBullet>
                        {withStar('/signature-forms')}
                      </SidebarMenuItem>
                    )}
                    {hasPermission(permissions, ALL_PERMISSIONS.VIEW_PDF_ENVELOPES) && (
                      <SidebarMenuItem to='/pdf-envelopes' title='PDF Signing' hasBullet>
                        {withStar('/pdf-envelopes')}
                      </SidebarMenuItem>
                    )}
                  </SidebarMenuItemWithSub>
                )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_INTEGRATION) && (
                <SidebarMenuItem to='/integrations' title='Integrations' fontIcon='bi-diagram-3'>
                  {withStar('/integrations')}
                </SidebarMenuItem>
              )}
            </>
          )}
        </>
      )}

      {/* ── RESOURCES ─────────────────────────────────────────────────────── */}
      {(hasPermission(permissions, ALL_PERMISSIONS.VIEW_RESOURCE_CENTER) ||
        hasPermission(permissions, ALL_PERMISSIONS.VIEW_ACADEMY)) && (
        <>
          <SectionDivider />
          <SectionHeader
            title='Resources'
            isExpanded={isExpanded('resources')}
            onToggle={() => toggleSection('resources')}
          />
          {isExpanded('resources') && (
            <>
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_RESOURCE_CENTER) && (
                <SidebarMenuItem to='/resource-center' title='Resource Center' fontIcon='bi-folder2-open'>
                  {withStar('/resource-center')}
                </SidebarMenuItem>
              )}
              {hasPermission(permissions, ALL_PERMISSIONS.VIEW_ACADEMY) && (
                <SidebarMenuItem to='/academy' title='Academy' fontIcon='bi-mortarboard'>
                  {withStar('/academy')}
                </SidebarMenuItem>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

export { SidebarMenuMainNew }
